import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Zap,
  Target,
  Calendar,
  ArrowRight,
  Sparkles,
  Clock,
  Dumbbell,
  TrendingUp,
  Heart,
} from "lucide-react";

const features = [
  {
  icon: Sparkles,
  title: "AI-Powered Plans",
  description:
    "Get a training program tailored to your goals, experience, and schedule using Google Gemini AI.",
},
{
  icon: Target,
  title: "Goal-Oriented",
  description:
    "Whether you want to build muscle, lose fat, or get stronger — we optimize for your goal.",
},
{
  icon: Calendar,
  title: "Flexible Scheduling",
  description:
    "Plans that fit your lifestyle. Train 2 days or 6 — we adapt to you.",
},
{
  icon: Clock,
  title: "Time-Efficient",
  description:
    "Every workout is designed to maximize results in your available time.",
},
{
  icon: Dumbbell,
  title: "Exercise Library",
  description:
    "Access 1000+ exercises with GIF demonstrations and detailed instructions.",
},
{
  icon: TrendingUp,
  title: "Track Progress",
  description:
    "Log workouts, visualize your gains, and stay motivated with progress charts.",
},
];

const stats = [
{ value: "1000+", label: "Exercises" },
{ value: "AI", label: "Powered Plans" },
{ value: "100%", label: "Free to Start" },
];

export default function Home() {
const { user } = useAuth();

return (
  <div className="min-h-screen">
    {/* Hero Section */}
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-500/10 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border mb-8">
          <Zap className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-muted-foreground">
            AI-powered training by Jacaobtech Labs
          </span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
          Your Perfect
          <br />
          <span className="text-amber-500">Gym Plan</span> in
          Seconds
        </h1>

        <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
          Stop guessing. Get a personalized training program built by AI,
          tailored to your goals, experience, and schedule.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {user ? (
            <Link to="/dashboard">
              <Button size="lg" className="gap-2 bg-amber-500 text-black hover:bg-amber-600">
                Go to Dashboard
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/auth/sign-up">
                <Button size="lg" className="gap-2 bg-amber-500 text-black hover:bg-amber-600">
                  Get Started Free
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/exercises">
                <Button variant="secondary" size="lg">
                  Browse Exercises
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="flex justify-center gap-12 mt-16">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-amber-500">
                {stat.value}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Features Section */}
    <section className="py-20 px-6 bg-card/50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Why Choose Bodyz Gym?
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            We combine fitness expertise with AI to create programs that
            actually work for you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <Card
              key={feature.title}
              className="group border-border/50 hover:border-amber-500/50 transition-colors"
            >
              <div className="p-6">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center mb-4 group-hover:bg-amber-500/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">
                  {feature.description}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <Card className="p-8 md:p-12 border-amber-500/20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 mb-6">
            <Heart className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-amber-500">
              Join thousands of fitness enthusiasts
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Fitness?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Get your personalized AI training plan today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <Link to="/onboarding">
                <Button size="lg" className="bg-amber-500 text-black hover:bg-amber-600">
                  Create Your Plan
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth/sign-up">
                  <Button size="lg" className="bg-amber-500 text-black hover:bg-amber-600">
                    Start Free Trial
                  </Button>
                </Link>
                <Link to="/exercises">
                  <Button variant="outline" size="lg">
                    Explore Exercises
                  </Button>
                </Link>
              </>
            )}
          </div>
        </Card>
      </div>
    </section>
  </div>
);
}
